import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useBlocker } from "react-router-dom";
import {
  createUnsavedJournalRegistry,
  leaveVerdict,
  leavingScopes,
  shouldWarnOnUnload,
  type JournalSaveState,
  type JournalScope,
  type UnsavedJournalRegistry,
} from "@/lib/unsaved-journal";

// TES-978 (navigation and autosave) — if something cannot be autosaved, a
// clear warning must show before navigation so no journal text is lost.
//
// Journal cards report their save state with useUnsavedJournal(); this
// provider guards the three ways out of a page:
// - in-app navigation, including `?flik=` / `?mote=` changes (useBlocker —
//   which is why App mounts a data router);
// - closing or reloading the tab (beforeunload, the browser's own prompt);
// - a save still in flight is waited for rather than warned about, so the
//   meeting -> overview round trip stays silent when the text saves.

const RegistryContext = createContext<UnsavedJournalRegistry | null>(null);

/**
 * Reports one journal card's save state to the guard. A no-op outside the
 * provider, and the card drops out of the registry when it unmounts.
 * TES-1316 — `scope` says which navigations would lose it (JournalScope).
 */
export function useUnsavedJournal(
  state: JournalSaveState,
  scope: JournalScope = "page",
): void {
  const registry = useContext(RegistryContext);
  const id = useId();
  useEffect(() => {
    registry?.set(id, state, scope);
  }, [registry, id, state, scope]);
  useEffect(() => () => registry?.remove(id), [registry, id]);
}

// Below this, a save that the link click's own blur started finishes
// unseen; above it, the doctor sees that the click was heard.
const WAIT_NOTICE_MS = 400;

export function UnsavedJournalProvider({ children }: { children: ReactNode }) {
  const [registry] = useState(createUnsavedJournalRegistry);
  // TES-1316 — the scopes the held navigation leaves, so the verdict below
  // weighs only what that navigation would lose.
  const heldScopes = useRef<JournalScope[]>([]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const scopes = leavingScopes(currentLocation, nextLocation);
    if (scopes.length === 0) return false;
    if (leaveVerdict(registry.states(scopes)) === "proceed") return false;
    heldScopes.current = scopes;
    return true;
  });

  // While a navigation is held: let it through the moment nothing would be
  // lost (the save landed, or the card is gone), otherwise ask.
  const verdict = useSyncExternalStore(registry.subscribe, () =>
    leaveVerdict(registry.states(heldScopes.current)),
  );
  const blocked = blocker.state === "blocked";
  useEffect(() => {
    if (blocker.state === "blocked" && verdict === "proceed") blocker.proceed();
  }, [blocker, verdict]);

  const [showWait, setShowWait] = useState(false);
  useEffect(() => {
    if (!blocked) return;
    const timer = window.setTimeout(() => setShowWait(true), WAIT_NOTICE_MS);
    return () => {
      window.clearTimeout(timer);
      setShowWait(false);
    };
  }, [blocked]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!shouldWarnOnUnload(registry.states())) return;
      e.preventDefault();
      // Older browsers only prompt when returnValue is set.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [registry]);

  const open =
    blocked && (verdict === "warn" || (verdict === "wait" && showWait));
  // TES-1316 — the journal itself is saved; only the meeting's unpublished
  // post-meeting steps would be lost.
  const onlyMeetingSteps =
    open &&
    leaveVerdict(
      registry.states(heldScopes.current.filter((s) => s === "page")),
    ) === "proceed";

  return (
    <RegistryContext.Provider value={registry}>
      {children}
      {open ? (
        <LeaveDialog
          waiting={verdict === "wait"}
          meetingSteps={onlyMeetingSteps}
          onStay={() => blocker.reset?.()}
          onLeave={() => blocker.proceed?.()}
        />
      ) : null}
    </RegistryContext.Provider>
  );
}

function LeaveDialog({
  waiting,
  meetingSteps,
  onStay,
  onLeave,
}: {
  waiting: boolean;
  meetingSteps: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  const stayRef = useRef<HTMLButtonElement>(null);
  // Staying is the safe answer, so it is the one Enter and Escape give.
  useEffect(() => {
    stayRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-md z-50">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="unsaved-journal-title"
        aria-describedby="unsaved-journal-body"
        onKeyDown={(e) => {
          if (e.key === "Escape") onStay();
        }}
        className="bg-surface rounded-md p-lg max-w-md w-full flex flex-col gap-md"
      >
        <h3 id="unsaved-journal-title" className="font-display text-xl">
          {waiting
            ? "Sparar journalen…"
            : meetingSteps
              ? "Eftermötesstegen är inte publicerade"
              : "Journaltexten är inte sparad"}
        </h3>
        <p id="unsaved-journal-body" className="text-textSecondary text-sm">
          {waiting
            ? "Vänta tills journaltexten är sparad, så går du vidare direkt."
            : meetingSteps
              ? "Journalanteckningen är sparad, men utfall, besked och bekräftelser sparas först när du publicerar. Lämnar du mötet nu försvinner de. Lämna ändå?"
              : "Journaltexten är inte sparad. Lämnar du sidan nu försvinner det du skrivit sedan senaste sparningen. Lämna ändå?"}
        </p>
        <div className="flex gap-sm justify-end">
          <button
            ref={stayRef}
            type="button"
            onClick={onStay}
            className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90"
          >
            Stanna kvar
          </button>
          {waiting ? null : (
            <button
              type="button"
              onClick={onLeave}
              className="px-md py-sm rounded-md text-danger hover:bg-danger/10 text-sm"
            >
              Lämna utan att spara
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
